import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatPercent } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ReferenceLine, Area, ComposedChart
} from "recharts";
import { Loader2, Plus, Trash2, Target, Trophy, TrendingUp, CheckCircle, AlertCircle } from "lucide-react";

export const ObjectivesPage = () => {
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [objectiveDialogOpen, setObjectiveDialogOpen] = useState(false);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  
  const [objectiveData, setObjectiveData] = useState({
    year: new Date().getFullYear(),
    seller_id: "",
    target_ca: "",
    objective_type: "seller"
  });
  
  const [bonusData, setBonusData] = useState({
    seller_id: "",
    year: new Date().getFullYear(),
    threshold_amount: "",
    bonus_amount: "",
    description: ""
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 2 }, (_, i) => currentYear + 1 - i);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sellersRes, objectivesRes, bonusesRes, progressRes] = await Promise.all([
        api.getSellers(),
        api.getAnnualObjectives({ year: selectedYear }),
        api.getSellerBonuses({ year: selectedYear }),
        api.getObjectivesProgress({ year: selectedYear })
      ]);
      setSellers(sellersRes.data);
      setObjectives(objectivesRes.data);
      setBonuses(bonusesRes.data);
      setProgressData(progressRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateObjective = async (e) => {
    e.preventDefault();
    try {
      await api.createAnnualObjective({
        year: parseInt(objectiveData.year),
        seller_id: objectiveData.objective_type === "seller" ? objectiveData.seller_id : null,
        target_ca: parseFloat(objectiveData.target_ca),
        objective_type: objectiveData.objective_type
      });
      toast.success("Objectif créé");
      setObjectiveDialogOpen(false);
      setObjectiveData({ year: currentYear, seller_id: "", target_ca: "", objective_type: "seller" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteObjective = async (id) => {
    if (!window.confirm("Supprimer cet objectif ?")) return;
    try {
      await api.deleteAnnualObjective(id);
      toast.success("Objectif supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCreateBonus = async (e) => {
    e.preventDefault();
    try {
      await api.createSellerBonus({
        seller_id: bonusData.seller_id,
        year: parseInt(bonusData.year),
        threshold_amount: parseFloat(bonusData.threshold_amount),
        bonus_amount: parseFloat(bonusData.bonus_amount),
        description: bonusData.description || null
      });
      toast.success("Prime créée");
      setBonusDialogOpen(false);
      setBonusData({ seller_id: "", year: currentYear, threshold_amount: "", bonus_amount: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteBonus = async (id) => {
    if (!window.confirm("Supprimer cette prime ?")) return;
    try {
      await api.deleteSellerBonus(id);
      toast.success("Prime supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Calculate monthly target line for chart
  const getChartData = () => {
    if (!progressData?.monthly_progression) return [];
    
    const globalObjective = progressData.objectives.find(o => o.objective_type === "global");
    const monthlyTarget = globalObjective ? globalObjective.target_ca / 12 : 0;
    
    let cumulativeTarget = 0;
    return progressData.monthly_progression.map((item, index) => {
      cumulativeTarget += monthlyTarget;
      return {
        ...item,
        target_cumulative: Math.round(cumulativeTarget),
        monthly_target: Math.round(monthlyTarget)
      };
    });
  };

  const formatPeriod = (period) => {
    if (period?.includes("-")) {
      const [year, month] = period.split("-");
      const monthNames = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    }
    return period;
  };

  return (
    <div className="space-y-6 animate-in" data-testid="objectives-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Objectifs & Primes</h1>
          <p className="text-muted-foreground mt-1">
            Définissez des objectifs annuels et des primes pour vos vendeurs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="objectives-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Progress Overview */}
          {progressData?.objectives?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {progressData.objectives.map((obj) => (
                <Card 
                  key={obj.objective_id} 
                  className={`card-hover ${obj.above_target ? "border-green-500" : ""}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-primary" />
                        {obj.seller_name === "Inconnu" ? (
                          <span className="text-amber-700 italic">Objectif orphelin</span>
                        ) : (
                          obj.seller_name || "Global"
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        {obj.above_target ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Atteint
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            En cours
                          </Badge>
                        )}
                        {obj.seller_name === "Inconnu" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDeleteObjective(obj.objective_id)}
                            data-testid={`delete-orphan-${obj.objective_id}`}
                            title="Supprimer cet objectif orphelin"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progression</span>
                        <span className="font-medium">{obj.progress_percent}%</span>
                      </div>
                      <Progress value={Math.min(obj.progress_percent, 100)} className="h-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Réalisé</p>
                        <p className="font-heading font-bold text-lg currency">{formatCurrency(obj.current_ca)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Objectif</p>
                        <p className="font-heading font-bold text-lg currency">{formatCurrency(obj.target_ca)}</p>
                      </div>
                    </div>
                    {obj.remaining > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Reste à faire: <span className="font-medium">{formatCurrency(obj.remaining)}</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Chart */}
          {progressData?.monthly_progression?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Évolution vs Objectif {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis 
                      dataKey="period" 
                      tickFormatter={formatPeriod}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      labelFormatter={formatPeriod}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="cumulative_ca"
                      name="CA cumulé"
                      fill="#C05621"
                      fillOpacity={0.3}
                      stroke="#C05621"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="target_cumulative"
                      name="Objectif cumulé"
                      stroke="#2D3748"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="objectives" className="space-y-6">
            <TabsList>
              <TabsTrigger value="objectives" data-testid="tab-objectives">
                <Target className="h-4 w-4 mr-2" />
                Objectifs
              </TabsTrigger>
              <TabsTrigger value="bonuses" data-testid="tab-bonuses">
                <Trophy className="h-4 w-4 mr-2" />
                Primes
              </TabsTrigger>
            </TabsList>

            {/* Objectives Tab */}
            <TabsContent value="objectives" className="space-y-4">
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm("Supprimer tous les objectifs orphelins (dont le vendeur n'existe plus) ?")) return;
                    try {
                      const res = await api.cleanupOrphanObjectives();
                      toast.success(`${res.data.deleted_count} objectif(s) orphelin(s) supprimé(s)`);
                      fetchData();
                    } catch (e) {
                      toast.error("Erreur de nettoyage");
                    }
                  }}
                  data-testid="cleanup-orphans-btn"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Nettoyer les orphelins
                </Button>
                <Button onClick={() => setObjectiveDialogOpen(true)} data-testid="add-objective-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvel objectif
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  {objectives.length === 0 ? (
                    <div className="text-center py-12">
                      <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Aucun objectif défini pour {selectedYear}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Vendeur</th>
                            <th>Objectif CA</th>
                            <th>Année</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectives.map((obj) => (
                            <tr key={obj.id} data-testid={`objective-row-${obj.id}`}>
                              <td>
                                <Badge variant={obj.objective_type === "global" ? "default" : "outline"}>
                                  {obj.objective_type === "global" ? "Global" : "Vendeur"}
                                </Badge>
                              </td>
                              <td className="font-medium">
                                {obj.seller_name ? (
                                  obj.seller_name
                                ) : (
                                  <span className="text-amber-700 italic flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" /> Orphelin
                                  </span>
                                )}
                              </td>
                              <td className="currency font-medium text-primary">
                                {formatCurrency(obj.target_ca)}
                              </td>
                              <td>{obj.year}</td>
                              <td>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteObjective(obj.id)}
                                  data-testid={`delete-objective-${obj.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bonuses Tab */}
            <TabsContent value="bonuses" className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setBonusDialogOpen(true)} data-testid="add-bonus-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle prime
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  {bonuses.length === 0 ? (
                    <div className="text-center py-12">
                      <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Aucune prime définie pour {selectedYear}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Vendeur</th>
                            <th>Seuil à atteindre</th>
                            <th>Prime</th>
                            <th>Progression</th>
                            <th>Statut</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bonuses.map((bonus) => (
                            <tr key={bonus.id}>
                              <td className="font-medium">{bonus.seller_name}</td>
                              <td className="currency">{formatCurrency(bonus.threshold_amount)}</td>
                              <td className="currency font-medium text-green-600">
                                {formatCurrency(bonus.bonus_amount)}
                              </td>
                              <td>
                                <div className="w-32">
                                  <Progress 
                                    value={Math.min((bonus.current_amount / bonus.threshold_amount) * 100, 100)} 
                                    className="h-2"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatCurrency(bonus.current_amount)} / {formatCurrency(bonus.threshold_amount)}
                                  </p>
                                </div>
                              </td>
                              <td>
                                {bonus.achieved ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Atteint
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    En cours
                                  </Badge>
                                )}
                              </td>
                              <td className="text-sm text-muted-foreground">
                                {bonus.description || "-"}
                              </td>
                              <td>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteBonus(bonus.id)}
                                  data-testid={`delete-bonus-${bonus.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Objective Dialog */}
      <Dialog open={objectiveDialogOpen} onOpenChange={setObjectiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvel objectif annuel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateObjective} className="space-y-4">
            <div className="space-y-2">
              <Label>Type d'objectif</Label>
              <Select
                value={objectiveData.objective_type}
                onValueChange={(v) => setObjectiveData({ ...objectiveData, objective_type: v, seller_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (toute l'entreprise)</SelectItem>
                  <SelectItem value="seller">Par vendeur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {objectiveData.objective_type === "seller" && (
              <div className="space-y-2">
                <Label>Vendeur</Label>
                <Select
                  value={objectiveData.seller_id}
                  onValueChange={(v) => setObjectiveData({ ...objectiveData, seller_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un vendeur" />
                  </SelectTrigger>
                  <SelectContent>
                    {sellers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Année</Label>
              <Select
                value={String(objectiveData.year)}
                onValueChange={(v) => setObjectiveData({ ...objectiveData, year: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Objectif CA (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={objectiveData.target_ca}
                onChange={(e) => setObjectiveData({ ...objectiveData, target_ca: e.target.value })}
                placeholder="Ex: 500000"
                required
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setObjectiveDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bonus Dialog */}
      <Dialog open={bonusDialogOpen} onOpenChange={setBonusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle prime d'objectif</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBonus} className="space-y-4">
            <div className="space-y-2">
              <Label>Vendeur</Label>
              <Select
                value={bonusData.seller_id}
                onValueChange={(v) => setBonusData({ ...bonusData, seller_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un vendeur" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Année</Label>
              <Select
                value={String(bonusData.year)}
                onValueChange={(v) => setBonusData({ ...bonusData, year: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Seuil à atteindre (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bonusData.threshold_amount}
                onChange={(e) => setBonusData({ ...bonusData, threshold_amount: e.target.value })}
                placeholder="Ex: 100000"
                required
              />
              <p className="text-xs text-muted-foreground">
                Montant de commandes que le vendeur doit atteindre pour débloquer la prime
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Prime (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bonusData.bonus_amount}
                onChange={(e) => setBonusData({ ...bonusData, bonus_amount: e.target.value })}
                placeholder="Ex: 5000"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Input
                value={bonusData.description}
                onChange={(e) => setBonusData({ ...bonusData, description: e.target.value })}
                placeholder="Ex: Prime trimestrielle Q1"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBonusDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ObjectivesPage;
